import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, CheckCircle, Loader2, Mic2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api.js";
import type { InboundBookingInput } from "@guestflow/shared/schemas";

export const Route = createFileRoute("/booking")({
  component: BookingPage,
});

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().optional(),
  bio: z.string().min(1, "Bio is required"),
  topics: z.string().optional(),
  twitter: z.string().optional(),
  linkedin: z.string().optional(),
  instagram: z.string().optional(),
  website: z.string().optional(),
  previousPodcasts: z.string().optional(),
  availability: z.string().optional(),
  additionalMessage: z.string().optional(),
  // Honeypot field - should remain empty
  websiteUrl: z.string().optional(),
});

type FormErrors = Partial<Record<keyof InboundBookingInput, string>>;

function BookingPage() {
  const [form, setForm] = useState<InboundBookingInput & { websiteUrl?: string; availability?: string; additionalMessage?: string }>({
    name: "",
    email: "",
    phone: "",
    bio: "",
    topics: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    website: "",
    previousPodcasts: "",
    availability: "",
    additionalMessage: "",
    websiteUrl: "", // Honeypot field
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validateField = (name: keyof InboundBookingInput, value: string) => {
    try {
      formSchema.shape[name].parse(value);
      return "";
    } catch (err) {
      if (err instanceof z.ZodError) {
        return err.errors[0]?.message || "Invalid";
      }
      return "";
    }
  };

  const handleChange = (field: keyof InboundBookingInput) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    
    // Clear error when user types
    if (errors[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field: keyof InboundBookingInput) => (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const error = validateField(field, e.target.value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(formSchema.shape) as Array<keyof InboundBookingInput>).forEach((key) => {
      const error = validateField(key, form[key] || "");
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/api/guests/inbound", form);
      
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        throw new Error(data.error || "Failed to submit booking request");
      }

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-12 pb-12 px-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Booking Request Submitted!
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                Thank you for your interest! We've received your information and will review it soon. 
                We'll be in touch via email to discuss next steps.
              </p>
              <Button
                onClick={() => window.location.href = "/"}
                className="bg-primary hover:bg-primary/90"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 group">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 group-hover:scale-110 transition-transform">
              <Users className="h-5 w-5" />
            </div>
            <span className="font-black text-2xl tracking-tight italic">Guestflow</span>
          </a>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mic2 className="h-4 w-4" />
            <span className="text-sm font-medium">Guest Booking</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Be a Guest on Our Podcast
            </h1>
            <p className="text-lg text-gray-600 max-w-lg mx-auto">
              Tell us about yourself and what you'd like to share with our audience. 
              We can't wait to hear your story!
            </p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-primary/5 border-b border-primary/10">
              <CardTitle className="text-xl">Your Information</CardTitle>
              <CardDescription>
                Fill out the form below to submit your booking request
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {submitError && (
                <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={handleChange("name")}
                        onBlur={handleBlur("name")}
                        placeholder="Jane Doe"
                        aria-invalid={!!errors.name}
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange("email")}
                        onBlur={handleBlur("email")}
                        placeholder="you@example.com"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* About You */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    About You
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="bio">
                      Bio / About You <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="bio"
                      value={form.bio}
                      onChange={handleChange("bio")}
                      onBlur={handleBlur("bio")}
                      placeholder="Tell us about your background, expertise, and what makes you unique..."
                      rows={5}
                      aria-invalid={!!errors.bio}
                    />
                    {errors.bio && (
                      <p className="text-xs text-destructive">{errors.bio}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      This helps us understand your story and how you'd fit with our audience.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="topics">Topics You Can Speak About</Label>
                    <Textarea
                      id="topics"
                      value={form.topics}
                      onChange={handleChange("topics")}
                      placeholder="e.g., Entrepreneurship, AI Technology, Leadership, Marketing..."
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Separate topics with commas for better organization.
                    </p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Online Presence
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="twitter">Twitter / X</Label>
                      <Input
                        id="twitter"
                        value={form.twitter}
                        onChange={handleChange("twitter")}
                        placeholder="@username or https://x.com/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="linkedin">LinkedIn</Label>
                      <Input
                        id="linkedin"
                        value={form.linkedin}
                        onChange={handleChange("linkedin")}
                        placeholder="https://linkedin.com/in/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram">Instagram</Label>
                      <Input
                        id="instagram"
                        value={form.instagram}
                        onChange={handleChange("instagram")}
                        placeholder="@username or https://instagram.com/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={form.website}
                        onChange={handleChange("website")}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Previous Appearances */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Experience
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="previousPodcasts">
                      Previous Podcast Appearances (Optional)
                    </Label>
                    <Textarea
                      id="previousPodcasts"
                      value={form.previousPodcasts}
                      onChange={handleChange("previousPodcasts")}
                      placeholder="List any podcasts you've been on before, with links if available..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Availability & Additional Info */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Availability & Notes
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="availability">
                      Your Availability (Optional)
                    </Label>
                    <Textarea
                      id="availability"
                      value={form.availability}
                      onChange={handleChange("availability")}
                      placeholder="e.g., Weekdays after 2pm, Tuesdays and Thursdays..."
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Let us know what times work best for you.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additionalMessage">
                      Additional Message (Optional)
                    </Label>
                    <Textarea
                      id="additionalMessage"
                      value={form.additionalMessage}
                      onChange={handleChange("additionalMessage")}
                      placeholder="Anything else you'd like us to know?"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Honeypot field - hidden from humans, visible to bots */}
                <div className="hidden" aria-hidden="true">
                  <Label htmlFor="websiteUrl">Website URL</Label>
                  <Input
                    id="websiteUrl"
                    type="text"
                    value={form.websiteUrl}
                    onChange={handleChange("websiteUrl")}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Submit */}
                <div className="pt-6 border-t">
                  <Button
                    type="submit"
                    className="w-full h-12 text-lg font-semibold"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Booking Request"
                    )}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    By submitting, you agree to be contacted about your booking request.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <footer className="mt-12 text-center text-sm text-muted-foreground">
            <p>Powered by Guestflow — the podcast guest CRM</p>
          </footer>
        </div>
      </main>
    </div>
  );
}
